import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_JobTracking_FindTemplateTable_VM_Row } from 'ab-api/web/jobTracking/findJobTrackingTemplateTable';

import TableNameEnum from 'ab-enums/tableName.enum';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { Button } from '@acceligentllc/storybook';

import Radio from 'af-components/Controls/Radio';
import CustomModal from 'af-components/CustomModal';
import TableNew from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import TextCell from 'af-components/Table/Cells/TextCell';
import type { TableProps } from 'af-components/Table/types';

import * as JobTemplateActions from 'af-actions/jobTracking';

import styles from './styles.module.scss';

interface OwnProps {
	show: boolean;
	onClose: () => void;
	onSave: (templateId: number) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const AssignTemplateModal: React.FC<Props> = (props) => {
	const {
		show,
		onClose,
		fetchTemplatesTable,
		onSave,
	} = props;

	const [chosenTemplateId, setChosenTemplateId] = React.useState<Nullable<number>>(null);

	const tableRef = React.useRef<TableRef<W_JobTracking_FindTemplateTable_VM_Row>>(null);

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery): Promise<TableContent<W_JobTracking_FindTemplateTable_VM_Row>> => {
		const { page, filterByText, sortBy } = tableRequestModel;
		const result = await fetchTemplatesTable(
			new TableQuery({ page, pageSize: 1000, sortBy, filterByText })
		);
		return result;
	}, [fetchTemplatesTable]);

	const handleRadioClick = React.useCallback((id: number) => () => {
		setChosenTemplateId(id);
	}, []);

	const renderNameCell = React.useCallback(
		() =>
			(cell: CellContext<W_JobTracking_FindTemplateTable_VM_Row, string>) => {
				return (
					<div className={styles['name-cell']}>
						<Radio checked={chosenTemplateId === cell.row.original.id} onCheck={handleRadioClick(cell.row.original.id)} />
						<TextCell value={cell.getValue() ?? '-'} />
					</div>
				);
			},
		[chosenTemplateId, handleRadioClick]
	);

	const renderTextCell = React.useCallback(() => (cell: CellContext<W_JobTracking_FindTemplateTable_VM_Row, string>) => {
		return <TextCell value={cell.getValue() ?? '-'} />;
	}, []);

	const renderColumnsCell = React.useCallback(() => (cell: CellContext<W_JobTracking_FindTemplateTable_VM_Row, string[]>) => {
		return <TextCell value={cell.getValue().join('\n')} />;
	}, []);

	const columns: TableProps<W_JobTracking_FindTemplateTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			accessor: 'name',
			header: 'Name',
			cell: renderNameCell(),
			enableSorting: true,
			enableHiding: true,
			size: 100,
		},
		{
			id: 'description',
			accessor: 'description',
			header: 'Description',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,

		},
		{
			id: 'columns',
			accessor: 'columns',
			header: 'Columns',
			cell: renderColumnsCell(),
			enableSorting: false,
			enableHiding: true,
		},
		{
			id: 'createdAt',
			accessor: 'createdAt',
			header: 'Date Submitted',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
		},
	], [renderColumnsCell, renderNameCell, renderTextCell]);

	const handleRowClick = React.useCallback((row: Row<W_JobTracking_FindTemplateTable_VM_Row>) => {
		setChosenTemplateId(row.original.id);
	}, []);

	const handleSave = React.useCallback(() => {
		if (chosenTemplateId) {
			onSave(chosenTemplateId);
		}
	}, [chosenTemplateId, onSave]);

	return (
		<CustomModal closeModal={onClose} modalStyle="info" showModal={show} size="lg">
			<CustomModal.Header closeModal={onClose} title="Use Job Tracking Template" />
			<CustomModal.Body padding="none">
				<TableNew
					columns={columns}
					fetch={fetchRows}
					hasSearchInput={true}
					hasSubRows={false}
					offsetHeight={200}
					onRowClick={handleRowClick}
					ref={tableRef}
					selectable={false}
					showFooter={false}
					tableName={TableNameEnum.JOB_TRACKING_TEMPLATES}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button label="Close" onClick={onClose} style="secondary" />
				<Button disabled={!chosenTemplateId} label="Use Template" onClick={handleSave} style="primary" />
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		fetchTemplatesTable: JobTemplateActions.findAllTemplatesForCompanyTable,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(AssignTemplateModal);

